import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { api } from "../api";
import type { FeatureList, PackagePlan, PackagePlanKind } from "../types";
import { AddPlanForm, type PlanFormData } from "./AddPlanForm";

const emptyForm = {
  name: "",
  description: "",
  planKind: "hosting" as PackagePlanKind,
  maxAccounts: 1,
  maxDomains: 3,
  maxSubdomains: 10,
  maxAddonDomains: 2,
  diskMb: 5120,
  maxDatabases: 3,
  maxEmails: 10,
  maxFtpAccounts: 3,
  bandwidthMb: 51200,
  priceMonthly: 0,
  priceYearly: 0,
  currency: "USD",
  sortOrder: 0,
  isDefault: false,
  active: true,
  featureListId: "" as string
};

type CatalogItem = { id: string; label: string; description: string };

function money(n: number, currency = "USD") {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: 2
    }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}

/**
 * Owner Packages ONLY — commercial seat licenses (User VPS / Reseller).
 * Pricing + max accounts. NOT RAM/CPU hosting plans (see Hosting Plans page).
 */
export function PackagesPage({
  packages,
  isStaff,
  onCreate,
  onUpdate,
  onDelete,
  onRefresh
}: {
  packages: PackagePlan[];
  isStaff?: boolean;
  onCreate: (data: typeof emptyForm) => Promise<void>;
  onUpdate: (id: string, data: Partial<typeof emptyForm>) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onRefresh: () => Promise<void>;
}) {
  const [tab, setTab] = useState<"hosting" | "reseller" | "features">(
    "hosting"
  );
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const [featureLists, setFeatureLists] = useState<FeatureList[]>([]);
  const [catalog, setCatalog] = useState<CatalogItem[]>([]);
  const [flForm, setFlForm] = useState({
    name: "",
    description: "",
    isDefault: false,
    features: {} as Record<string, boolean>
  });
  const [flEditId, setFlEditId] = useState<string | null>(null);
  const [showFl, setShowFl] = useState(false);

  const hostingPkgs = useMemo(
    () =>
      packages.filter(
        (p) => (p.planKind || "hosting") === "hosting" || p.ownerResellerId
      ),
    [packages]
  );
  const resellerPkgs = useMemo(
    () =>
      packages.filter(
        (p) => p.planKind === "reseller" && !p.ownerResellerId
      ),
    [packages]
  );

  const loadFeatures = useCallback(async () => {
    if (!isStaff) return;
    try {
      const [fl, cat] = await Promise.all([
        api<{ items: FeatureList[] }>("/api/v1/feature-lists"),
        api<{ items: CatalogItem[] }>("/api/v1/feature-lists/catalog")
      ]);
      setFeatureLists(fl.items || []);
      setCatalog(cat.items || []);
      if (!form.featureListId && fl.items?.length) {
        const def = fl.items.find((x) => x.isDefault) || fl.items[0];
        if (def) setForm((f) => ({ ...f, featureListId: def.id }));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [isStaff, form.featureListId]);

  useEffect(() => {
    void loadFeatures();
  }, [loadFeatures]);

  const startEdit = (p: PackagePlan) => {
    setEditId(p.id);
    setForm({
      name: p.name,
      description: p.description,
      planKind: (p.planKind || "hosting") as PackagePlanKind,
      maxAccounts: p.maxAccounts ?? 1,
      maxDomains: p.maxDomains,
      maxSubdomains: p.maxSubdomains,
      maxAddonDomains: p.maxAddonDomains ?? 0,
      diskMb: p.diskMb,
      maxDatabases: p.maxDatabases,
      maxEmails: p.maxEmails,
      maxFtpAccounts: p.maxFtpAccounts,
      bandwidthMb: p.bandwidthMb,
      priceMonthly: p.priceMonthly ?? 0,
      priceYearly: p.priceYearly ?? 0,
      currency: p.currency || "USD",
      sortOrder: p.sortOrder ?? 0,
      isDefault: p.isDefault,
      active: p.active !== false,
      featureListId: p.featureListId || ""
    });
    setShow(true);
    setTab(p.planKind === "reseller" ? "reseller" : "hosting");
  };

  const openNew = (kind: PackagePlanKind) => {
    setEditId(null);
    setForm({
      ...emptyForm,
      planKind: kind,
      maxAccounts: kind === "reseller" ? 100 : 1,
      maxDomains: kind === "reseller" ? 500 : 1,
      maxFtpAccounts: kind === "reseller" ? 0 : 2,
      featureListId:
        featureLists.find((f) => f.isDefault)?.id || featureLists[0]?.id || ""
    });
    setShow(true);
    setTab(kind === "reseller" ? "reseller" : "hosting");
  };

  const submitPlan = async (data: PlanFormData) => {
    setBusy(true);
    setError("");
    setMsg("");
    try {
      let featureListId = data.featureListId || null;
      // Manual feature checkboxes → create/update a feature list bound to this plan name
      if (data.planKind === "hosting" && data.features && isStaff) {
        const flName = `${data.name.trim()} features`;
        const existing = featureLists.find(
          (f) => f.name.toLowerCase() === flName.toLowerCase()
        );
        if (existing) {
          await api(`/api/v1/feature-lists/${existing.id}`, {
            method: "PATCH",
            body: JSON.stringify({
              name: flName,
              description: `Features for plan ${data.name}`,
              features: data.features
            })
          });
          featureListId = existing.id;
        } else {
          const created = await api<{
            ok: boolean;
            featureList?: FeatureList;
            item?: FeatureList;
          }>("/api/v1/feature-lists", {
            method: "POST",
            body: JSON.stringify({
              name: flName,
              description: `Features for plan ${data.name}`,
              isDefault: false,
              features: data.features
            })
          });
          featureListId =
            created.featureList?.id || created.item?.id || featureListId;
        }
        await loadFeatures();
      }

      const payload = {
        name: data.name,
        description: data.description,
        planKind: data.planKind,
        maxAccounts: data.maxAccounts,
        maxDomains: data.maxDomains,
        maxSubdomains: data.maxSubdomains,
        maxAddonDomains: data.maxAddonDomains,
        diskMb: data.diskMb > 0 ? data.diskMb : 0,
        maxDatabases: data.maxDatabases,
        maxEmails: data.maxEmails,
        maxFtpAccounts: data.maxFtpAccounts,
        bandwidthMb: data.bandwidthMb,
        priceMonthly: data.priceMonthly,
        priceYearly: data.priceYearly,
        currency: data.currency,
        sortOrder: data.sortOrder,
        isDefault: data.isDefault,
        active: data.active,
        featureListId: featureListId || ""
      };

      if (editId) await onUpdate(editId, payload);
      else await onCreate(payload);
      setShow(false);
      setEditId(null);
      setForm(emptyForm);
      setMsg(editId ? "Plan updated" : "Plan created");
      await onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const startFlEdit = (fl: FeatureList) => {
    setFlEditId(fl.id);
    setFlForm({
      name: fl.name,
      description: fl.description,
      isDefault: fl.isDefault,
      features: { ...fl.features }
    });
    setShowFl(true);
  };

  const startFlNew = () => {
    setFlEditId(null);
    const features: Record<string, boolean> = {};
    for (const c of catalog) features[c.id] = true;
    setFlForm({
      name: "",
      description: "",
      isDefault: false,
      features
    });
    setShowFl(true);
  };

  const submitFl = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setMsg("");
    try {
      if (flEditId) {
        await api(`/api/v1/feature-lists/${flEditId}`, {
          method: "PATCH",
          body: JSON.stringify(flForm)
        });
        setMsg("Feature list updated");
      } else {
        await api("/api/v1/feature-lists", {
          method: "POST",
          body: JSON.stringify(flForm)
        });
        setMsg("Feature list created");
      }
      setShowFl(false);
      setFlEditId(null);
      await loadFeatures();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const deleteFl = async (id: string, name: string) => {
    if (!confirm(`Delete feature list “${name}”?`)) return;
    setError("");
    try {
      await api(`/api/v1/feature-lists/${id}`, { method: "DELETE" });
      setMsg("Feature list deleted");
      await loadFeatures();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const list =
    tab === "reseller" ? resellerPkgs : tab === "hosting" ? hostingPkgs : [];

  const renderPlanCards = (items: PackagePlan[]) => (
    <div className="rp-pkg-list">
      {items.map((p) => (
        <article key={p.id} className="rp-pkg-row">
          <div>
            <h3 className="rp-pkg-name">
              {p.name}
              {p.isDefault ? (
                <span
                  className="rp-status rp-status-info"
                  style={{ marginLeft: 8, verticalAlign: "middle" }}
                >
                  Default
                </span>
              ) : null}
              {p.active === false ? (
                <span
                  className="rp-status rp-status-muted"
                  style={{ marginLeft: 6, verticalAlign: "middle" }}
                >
                  Archived
                </span>
              ) : (
                <span
                  className="rp-status rp-status-ok"
                  style={{ marginLeft: 6, verticalAlign: "middle" }}
                >
                  Active
                </span>
              )}
            </h3>
            <p className="rp-pkg-type">
              {p.planKind === "reseller"
                ? "Reseller Package · Server/IP License"
                : "VPS Package · Server/IP License"}
              {p.description ? ` · ${p.description}` : ""}
            </p>
          </div>
          <div className="rp-pkg-price">
            {money(p.priceMonthly || 0, p.currency || "USD")}
            <small>
              {money(p.priceYearly || 0, p.currency || "USD")} / year
            </small>
          </div>
          <div className="rp-pkg-limits">
            <span>
              <strong>
                {(p.maxAccounts || 1).toLocaleString()}
              </strong>{" "}
              account seats
            </span>
            <span>
              <strong>{p.maxDomains.toLocaleString()}</strong> soft domains
            </span>
            <span>
              <strong>
                {p.diskMb >= 1024
                  ? `${(p.diskMb / 1024).toFixed(0)} GB`
                  : `${p.diskMb} MB`}
              </strong>{" "}
              soft disk
            </span>
          </div>
          <div className="rp-pkg-actions">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => startEdit(p)}
            >
              Edit
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              style={{ color: "var(--danger)" }}
              onClick={() => {
                if (confirm(`Delete package ${p.name}?`)) void onDelete(p.id);
              }}
            >
              Delete
            </button>
          </div>
        </article>
      ))}
      {items.length === 0 ? (
        <div className="rp-empty">
          <strong>No packages in this category yet</strong>
          <p>Add a VPS or Reseller license package for server IPs.</p>
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="rp-page">
      <div className="rp-page-head">
        <div>
          <h1 className="rp-page-title">Package</h1>
          <p className="rp-page-sub">
            Manage VPS and Reseller licenses assigned to server IP addresses.
            Not the same as Hosting Plans (RAM/CPU for accounts).
          </p>
        </div>
        <div className="rp-page-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              void onRefresh();
              void loadFeatures();
            }}
          >
            Refresh
          </button>
          {tab === "hosting" || tab === "reseller" ? (
            <button
              type="button"
              className="btn btn-primary"
              style={{ width: "auto", marginTop: 0 }}
              onClick={() =>
                show && !editId
                  ? setShow(false)
                  : openNew(tab === "reseller" ? "reseller" : "hosting")
              }
            >
              {show && !editId
                ? "Close"
                : "+ Add Package"}
            </button>
          ) : isStaff ? (
            <button
              type="button"
              className="btn btn-primary"
              style={{ width: "auto", marginTop: 0 }}
              onClick={startFlNew}
            >
              + Feature list
            </button>
          ) : null}
        </div>
      </div>

      {msg ? <div className="success-banner">{msg}</div> : null}
      {error ? <div className="error-banner">{error}</div> : null}

      <div className="rp-pkg-tabs">
        <button
          type="button"
          className={`rp-pkg-tab${tab === "hosting" ? " is-active" : ""}`}
          onClick={() => {
            setTab("hosting");
            setShow(false);
          }}
        >
          VPS Packages · {hostingPkgs.length}
        </button>
        {isStaff ? (
          <button
            type="button"
            className={`rp-pkg-tab${tab === "reseller" ? " is-active" : ""}`}
            onClick={() => {
              setTab("reseller");
              setShow(false);
            }}
          >
            Reseller Packages · {resellerPkgs.length}
          </button>
        ) : null}
        {isStaff ? (
          <button
            type="button"
            className={`rp-pkg-tab${tab === "features" ? " is-active" : ""}`}
            onClick={() => {
              setTab("features");
              setShow(false);
            }}
          >
            Feature Lists
          </button>
        ) : null}
      </div>

      {(tab === "hosting" || tab === "reseller") && show ? (
        <AddPlanForm
          planKind={tab === "reseller" ? "reseller" : "hosting"}
          editMode={Boolean(editId)}
          initial={editId ? form : { planKind: tab === "reseller" ? "reseller" : "hosting" }}
          featureLists={featureLists}
          catalog={catalog}
          isStaff={isStaff}
          busy={busy}
          onSubmit={submitPlan}
          onCancel={() => {
            setShow(false);
            setEditId(null);
          }}
        />
      ) : null}

      {tab === "hosting" || tab === "reseller" ? renderPlanCards(list) : null}

      {tab === "features" && isStaff ? (
        <>
          {showFl ? (
            <form className="panel-form" onSubmit={(e) => void submitFl(e)}>
              <div className="panel-form-title">
                {flEditId ? "Edit feature list" : "New feature list"}
              </div>
              <div className="form-grid">
                <div className="field">
                  <label>Name</label>
                  <input
                    value={flForm.name}
                    onChange={(e) =>
                      setFlForm((f) => ({ ...f, name: e.target.value }))
                    }
                    required
                  />
                </div>
                <div className="field">
                  <label>Description</label>
                  <input
                    value={flForm.description}
                    onChange={(e) =>
                      setFlForm((f) => ({
                        ...f,
                        description: e.target.value
                      }))
                    }
                  />
                </div>
                <div className="field checkbox-field">
                  <label>
                    <input
                      type="checkbox"
                      checked={flForm.isDefault}
                      onChange={(e) =>
                        setFlForm((f) => ({
                          ...f,
                          isDefault: e.target.checked
                        }))
                      }
                    />{" "}
                    Default feature list
                  </label>
                </div>
              </div>
              <div className="panel-form-title" style={{ marginTop: 16 }}>
                cPanel tools enabled
              </div>
              <div className="feature-check-grid">
                {catalog.map((c) => (
                  <label key={c.id} className="feature-check">
                    <input
                      type="checkbox"
                      checked={flForm.features[c.id] !== false}
                      onChange={(e) =>
                        setFlForm((f) => ({
                          ...f,
                          features: {
                            ...f.features,
                            [c.id]: e.target.checked
                          }
                        }))
                      }
                    />
                    <span>
                      <strong>{c.label}</strong>
                      <small>{c.description}</small>
                    </span>
                  </label>
                ))}
              </div>
              <div className="row-actions" style={{ marginTop: 14 }}>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={busy}
                >
                  {busy ? "Saving…" : "Save feature list"}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setShowFl(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          ) : null}

          <div className="pkg-grid">
            {featureLists.map((fl) => (
              <section key={fl.id} className="card pkg-card">
                <div className="pkg-head">
                  <div>
                    <div className="card-value" style={{ fontSize: 18 }}>
                      {fl.name}
                    </div>
                    <p className="card-meta">{fl.description || "—"}</p>
                  </div>
                  {fl.isDefault ? (
                    <span className="badge primary">default</span>
                  ) : null}
                </div>
                <div className="chip-row" style={{ marginTop: 10 }}>
                  {Object.entries(fl.features)
                    .filter(([, on]) => on)
                    .map(([k]) => (
                      <span key={k} className="chip">
                        {catalog.find((c) => c.id === k)?.label || k}
                      </span>
                    ))}
                </div>
                <div className="row-actions" style={{ marginTop: 14 }}>
                  <button
                    type="button"
                    className="link-btn"
                    onClick={() => startFlEdit(fl)}
                  >
                    Edit
                  </button>
                  {!fl.isDefault ? (
                    <button
                      type="button"
                      className="link-btn danger"
                      onClick={() => void deleteFl(fl.id, fl.name)}
                    >
                      Delete
                    </button>
                  ) : null}
                </div>
              </section>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
